import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { getNumericStringSortComparator } from '../../utils/sortComparators';

interface UsePortfolioColumnsProps {
  autosizeColumns: boolean;
  isGroupingEnabled?: boolean;
}

export const usePortfolioColumns = ({
  autosizeColumns,
  isGroupingEnabled = false,
}: UsePortfolioColumnsProps) => {
  const columns: GridColDef[] = useMemo(
    () => [
      // Dedicated group column - only shown when grouping is enabled
      ...(isGroupingEnabled
        ? [
            {
              field: 'group_name',
              headerName: 'Group',
              ...(autosizeColumns ? {} : { flex: 1.2 }),
              sortable: false,
              filterable: false,
              pinnable: false, // Prevent user from unpinning
              hideable: false, // Prevent user from hiding
              renderCell: params => {
                // Only show group name for group rows
                if (params.row.isGroup && params.row.groupName) {
                  return params.row.groupName;
                }
                return '';
              },
            },
          ]
        : []),
      {
        field: 'client_name',
        headerName: 'Client Name',
        ...(autosizeColumns ? {} : { flex: 1.2 }),
        sortable: true,
        renderCell: params => {
          // For group rows, show empty (group name is now in dedicated column)
          if (params.row.isGroup) {
            return '';
          }
          // For regular rows, show the client name
          return params.row.client_name;
        },
      },
      {
        field: 'portfolio_name',
        headerName: 'Portfolio Name',
        ...(autosizeColumns ? {} : { flex: 1 }),
        sortable: true,
        renderCell: params => {
          // For group rows, show empty
          if (params.row.isGroup) {
            return '';
          }
          return params.row.portfolio_name;
        },
      },
      {
        field: 'portfolio_id',
        headerName: 'Portfolio ID',
        ...(autosizeColumns ? {} : { flex: 1 }),
        sortable: true,
        getSortComparator: getNumericStringSortComparator,
        renderCell: params => {
          // For group rows, show empty
          if (params.row.isGroup) {
            return '';
          }
          return params.row.portfolio_id;
        },
      },
      {
        field: 'leading_currency',
        headerName: 'Leading Currency',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        sortable: true,
        renderCell: params => {
          // For group rows, show empty
          if (params.row.isGroup) {
            return '';
          }
          return params.row.leading_currency;
        },
      },
    ],
    [autosizeColumns, isGroupingEnabled]
  );

  return {
    columns,
  };
};
